/**
 * Tops, bottoms, shoes. Authored in CLOTH_AUTHOR indices (+ fixed shoe
 * leather 3/7). Kinds differ by silhouette and hem rows, SNES-style.
 * No contour or shadow here — compose.js owns both.
 */
import { CLOTH_AUTHOR } from '../catalog.js';
import {
  canvas,
  fillRect,
  hLine,
  mirrorFrame,
  put,
  walkSheet,
} from '../../pixel-kit.js';
import { FOOT_Y, FRONT, SHOE_TOP, SIDE, poseFor } from './rig.js';

const SHOE_DK = 3;
const SHOE_MD = 7;
const [CLOTH_DK, CLOTH_MD, CLOTH_LT] = CLOTH_AUTHOR;

function blank() {
  return canvas(16, 24);
}

/* ---------------- shoes ---------------- */

function frontShoes(px, p) {
  const lTop = SHOE_TOP - p.liftL;
  const rTop = SHOE_TOP - p.liftR;
  fillRect(px, FRONT.shoeLX, lTop, 3, 2, SHOE_MD);
  hLine(px, FRONT.shoeLX, FRONT.shoeLX + 2, lTop + 2, SHOE_DK);
  fillRect(px, FRONT.shoeRX, rTop, 3, 2, SHOE_MD);
  hLine(px, FRONT.shoeRX, FRONT.shoeRX + 2, rTop + 2, SHOE_DK);
}

function sideShoes(px, p) {
  const nearX = SIDE.hipX + p.stride;
  const farX = SIDE.hipX - p.stride;
  // far shoe first so the near one overlaps it at the pass pose
  fillRect(px, farX - 1, SHOE_TOP, 3, 2, SHOE_DK);
  hLine(px, farX - 1, farX + 1, FOOT_Y, SHOE_DK);
  fillRect(px, nearX - 1, SHOE_TOP, 3, 2, SHOE_MD);
  hLine(px, nearX - 1, nearX + 1, FOOT_Y, SHOE_DK);
  put(px, nearX - 2, FOOT_Y, SHOE_DK); // toe, facing left
  put(px, nearX - 2, FOOT_Y - 1, SHOE_MD);
}

/* ---------------- bottoms ---------------- */

function frontBottom(kind, frame) {
  const px = blank();
  const p = poseFor('down', frame);
  const hemY = kind === 'shorts' ? 18 : SHOE_TOP - 1;
  if (kind === 'skirt') {
    fillRect(px, 5, 15 + p.bob, 6, 2, CLOTH_MD);
    fillRect(px, 4, 17 + p.bob, 8, 2, CLOTH_MD);
    hLine(px, 4, 11, 19 + p.bob, CLOTH_DK);
    hLine(px, 5, 7, 15 + p.bob, CLOTH_LT);
  } else {
    // waistband
    hLine(px, 5, 10, 15 + p.bob, CLOTH_DK);
    fillRect(px, 5, 16, 6, hemY - 16, CLOTH_MD);
    // inner-leg crease
    fillRect(px, 7, 17, 2, hemY - 17, CLOTH_DK);
    hLine(px, 5, 10, hemY, CLOTH_DK);
  }
  frontShoes(px, p);
  return px;
}

function sideBottom(kind, frame) {
  const px = blank();
  const p = poseFor('left', frame);
  const nearX = SIDE.hipX + p.stride;
  const farX = SIDE.hipX - p.stride;
  if (kind === 'skirt') {
    fillRect(px, 6, 15 + p.bob, 5, 2, CLOTH_MD);
    fillRect(px, 5, 17 + p.bob, 6, 2, CLOTH_MD);
    hLine(px, 5, 10, 19 + p.bob, CLOTH_DK);
    put(px, 6, 15 + p.bob, CLOTH_LT);
  } else {
    const hemY = kind === 'shorts' ? 18 : SHOE_TOP - 1;
    hLine(px, 6, 10, 15 + p.bob, CLOTH_DK);
    fillRect(px, farX, 16, 2, hemY - 15, CLOTH_DK);
    fillRect(px, nearX, 16, 2, hemY - 15, CLOTH_MD);
  }
  sideShoes(px, p);
  return px;
}

/* ---------------- tops ---------------- */

function frontTop(kind, frame) {
  const px = blank();
  const p = poseFor('down', frame);
  const bob = p.bob;
  const y0 = 10 + bob;

  fillRect(px, 5, y0, 6, 6, CLOTH_MD);
  hLine(px, 5, 10, y0 + 5, CLOTH_DK); // hem
  hLine(px, 5, 7, y0, CLOTH_LT); // lit shoulder cap

  if (kind === 'sleeveless') {
    // bare shoulders: notch the top corners back to skin
    put(px, 5, y0, 0);
    put(px, 10, y0, 0);
  } else if (kind === 'shirt') {
    fillRect(px, 3, y0 + p.armYL, 2, 3, CLOTH_MD);
    fillRect(px, 11, y0 + p.armYR, 2, 3, CLOTH_MD);
    hLine(px, 3, 4, y0 + 2 + p.armYL, CLOTH_DK);
    hLine(px, 11, 12, y0 + 2 + p.armYR, CLOTH_DK);
  } else {
    // hoodie: full sleeves, collar ridge, drawstrings, pocket
    fillRect(px, 3, y0 + p.armYL, 2, 4, CLOTH_MD);
    fillRect(px, 11, y0 + p.armYR, 2, 4, CLOTH_MD);
    hLine(px, 3, 4, y0 + 3 + p.armYL, CLOTH_DK);
    hLine(px, 11, 12, y0 + 3 + p.armYR, CLOTH_DK);
    hLine(px, 6, 9, y0, CLOTH_DK); // collar
    put(px, 7, y0 + 1, CLOTH_LT); put(px, 8, y0 + 1, CLOTH_LT); // drawstrings
    fillRect(px, 6, y0 + 4, 4, 1, CLOTH_DK); // pocket
  }
  return px;
}

function upTop(kind, frame) {
  const px = frontTop(kind === 'sleeveless' ? 'sleeveless' : kind, frame);
  const p = poseFor('up', frame);
  if (kind === 'hoodie') {
    // hood hangs over the back of the neck
    fillRect(px, 5, 8 + p.bob, 6, 2, CLOTH_MD);
    hLine(px, 5, 10, 9 + p.bob, CLOTH_DK);
    hLine(px, 5, 7, 8 + p.bob, CLOTH_LT);
  }
  return px;
}

function sideTop(kind, frame) {
  const px = blank();
  const p = poseFor('left', frame);
  const bob = p.bob;
  const y0 = 10 + bob;

  fillRect(px, 6, y0, 5, 6, CLOTH_MD);
  hLine(px, 6, 10, y0 + 5, CLOTH_DK);
  hLine(px, 6, 8, y0, CLOTH_LT);

  if (kind === 'shirt') {
    fillRect(px, 7, y0, 2, 3, CLOTH_MD);
    hLine(px, 7, 8, y0 + 2, CLOTH_DK);
  } else if (kind === 'hoodie') {
    fillRect(px, 7, y0, 2, 4, CLOTH_MD);
    hLine(px, 7, 8, y0 + 3, CLOTH_DK);
    // hood bulk at the back of the neck (back = +x when facing left)
    fillRect(px, 9, 8 + bob, 3, 2, CLOTH_MD);
    put(px, 11, 9 + bob, CLOTH_DK);
  }
  return px;
}

function drawTop(kind, direction, frame) {
  if (direction === 'left') return sideTop(kind, frame);
  if (direction === 'right') return mirrorFrame(sideTop(kind, frame));
  if (direction === 'up') return upTop(kind, frame);
  return frontTop(kind, frame);
}

function drawBottom(kind, direction, frame) {
  if (direction === 'left') return sideBottom(kind, frame);
  if (direction === 'right') return mirrorFrame(sideBottom(kind, frame));
  return frontBottom(kind, frame);
}

export function topSheet(kind) {
  return walkSheet((direction, frame) => drawTop(kind, direction, frame));
}

export function bottomSheet(kind) {
  return walkSheet((direction, frame) => drawBottom(kind, direction, frame));
}
